import React, { useCallback, useState } from 'react';
import { ActivitySquare, Save, RefreshCw, Download, Scale, Ruler, User } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import MeasurementsSection from './MeasurementsSection';
import PersonalDataSection from './PersonalDataSection';
import AdditionalParametersSection from './AdditionalParametersSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';
import { calculateWHR, getWHRCategory } from '../../../../../utils/calculations/health2';

/**
 * Компонент основной формы расчета индекса талия-бедра
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  whrCategories,
  activityLevels,
  saveResult 
}) => {
  
  // Состояние для имени сохраняемого результата
  const [saveResultName, setSaveResultName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // Если изменилась система измерения, конвертируем значения
      if (name === 'measurementSystem') {
        if (fieldValue === 'imperial' && prev.measurementSystem === 'metric') {
          // Конвертируем из метрических в имперские
          newState.waist = (parseFloatOrDefault(prev.waist) / 2.54).toFixed(1);
          newState.hip = (parseFloatOrDefault(prev.hip) / 2.54).toFixed(1);
          newState.height = (parseFloatOrDefault(prev.height) / 2.54).toFixed(1);
          newState.weight = (parseFloatOrDefault(prev.weight) * 2.20462).toFixed(1);
        } else if (fieldValue === 'metric' && prev.measurementSystem === 'imperial') {
          // Конвертируем из имперских в метрические
          newState.waist = (parseFloatOrDefault(prev.waist) * 2.54).toFixed(1);
          newState.hip = (parseFloatOrDefault(prev.hip) * 2.54).toFixed(1);
          newState.height = (parseFloatOrDefault(prev.height) * 2.54).toFixed(1);
          newState.weight = (parseFloatOrDefault(prev.weight) / 2.20462).toFixed(1);
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      waist, hip, age, height, weight, gender
    } = formData;

    // Парсинг числовых значений
    const numWaist = parseFloatOrDefault(waist, -1);
    const numHip = parseFloatOrDefault(hip, -1);
    const numAge = parseFloatOrDefault(age, -1);
    const numHeight = parseFloatOrDefault(height, -1);
    const numWeight = parseFloatOrDefault(weight, -1);

    // Валидация измерений
    if (numWaist <= 0) newErrors.waist = 'Обхват талии должен быть положительным числом.';
    if (!newErrors.waist) {
      if (formData.measurementSystem === 'metric' && numWaist > 200) {
        newErrors.waist = 'Значение слишком большое. Убедитесь, что вы указали обхват в сантиметрах.';
      } else if (formData.measurementSystem === 'imperial' && numWaist > 80) {
        newErrors.waist = 'Значение слишком большое. Убедитесь, что вы указали обхват в дюймах.';
      }
    }
    
    if (numHip <= 0) newErrors.hip = 'Обхват бедер должен быть положительным числом.';
    if (!newErrors.hip) {
      if (formData.measurementSystem === 'metric' && numHip > 200) {
        newErrors.hip = 'Значение слишком большое. Убедитесь, что вы указали обхват в сантиметрах.';
      } else if (formData.measurementSystem === 'imperial' && numHip > 80) {
        newErrors.hip = 'Значение слишком большое. Убедитесь, что вы указали обхват в дюймах.';
      }
    }
    
    // Валидация персональных данных
    if (numAge < 16) newErrors.age = 'Минимальный возраст для расчета - 16 лет.';
    if (numAge > 120) newErrors.age = 'Максимальный возраст - 120 лет.';
    
    if (numHeight <= 0) newErrors.height = 'Рост должен быть положительным числом.';
    if (!newErrors.height) {
      if (formData.measurementSystem === 'metric' && numHeight > 250) {
        newErrors.height = 'Значение слишком большое. Убедитесь, что вы указали рост в сантиметрах.';
      } else if (formData.measurementSystem === 'imperial' && numHeight > 96) {
        newErrors.height = 'Значение слишком большое. Убедитесь, что вы указали рост в дюймах.';
      }
    }
    
    if (numWeight <= 0) newErrors.weight = 'Вес должен быть положительным числом.';
    if (!newErrors.weight) {
      if (formData.measurementSystem === 'metric' && numWeight > 300) {
        newErrors.weight = 'Значение слишком большое. Убедитесь, что вы указали вес в килограммах.';
      } else if (formData.measurementSystem === 'imperial' && numWeight > 660) {
        newErrors.weight = 'Значение слишком большое. Убедитесь, что вы указали вес в фунтах.';
      }
    }

    // Дополнительные проверки
    if (!['male', 'female'].includes(gender)) {
      newErrors.gender = "Пожалуйста, выберите пол для точного расчета.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета WHR
  const calculateResults = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const waistValue = parseFloatOrDefault(formData.waist);
        const hipValue = parseFloatOrDefault(formData.hip);
        const ageValue = parseFloatOrDefault(formData.age);
        const heightValue = parseFloatOrDefault(formData.height);
        const weightValue = parseFloatOrDefault(formData.weight);
        const gender = formData.gender;
        const measurementSystem = formData.measurementSystem;
        const activityLevel = formData.activityLevel;

        // Расчет WHR
        const whrValue = calculateWHR(waistValue, hipValue);
        
        // Определение категории WHR
        const category = getWHRCategory(whrValue, gender, whrCategories);
        
        // Расчет ИМТ (BMI)
        const bmi = calculateBMI(weightValue, heightValue, measurementSystem);
        
        // Получение рекомендаций
        const recommendations = generateRecommendations(whrValue, bmi, gender, ageValue, activityLevel);
        
        // Расчет идеальных показателей
        const idealMeasurements = calculateIdealMeasurements(heightValue, gender, measurementSystem);
        
        // Готовим результаты
        const finalResults = {
          whr: whrValue,
          measurements: {
            waist: waistValue,
            hip: hipValue,
            waistToHeightRatio: waistValue / heightValue,
            measurementSystem
          },
          personalData: {
            age: ageValue,
            gender,
            height: heightValue,
            weight: weightValue,
            activityLevel
          },
          category,
          bmi,
          bmiCategory: getBMICategory(bmi),
          recommendations,
          idealMeasurements,
          date: new Date().toISOString()
        };

        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, whrCategories]);

  // Расчет ИМТ
  const calculateBMI = (weight, height, system) => {
    if (system === 'imperial') {
      // В имперской системе: вес в фунтах, рост в дюймах
      // Формула: (вес в фунтах * 703) / (рост в дюймах)²
      return (weight * 703) / (height * height);
    } else {
      // В метрической системе: вес в кг, рост в см
      // Формула: вес / (рост в метрах)²
      const heightInMeters = height / 100;
      return weight / (heightInMeters * heightInMeters);
    }
  };

  // Определение категории ИМТ
  const getBMICategory = (bmi) => {
    if (bmi < 16) {
      return {
        id: 'severe_thinness',
        name: 'Выраженный дефицит массы тела',
        color: '#d32f2f',
        risk: 'Высокий риск для здоровья'
      };
    } else if (bmi < 18.5) {
      return {
        id: 'thinness',
        name: 'Недостаточная масса тела',
        color: '#ff9800',
        risk: 'Повышенный риск для здоровья'
      };
    } else if (bmi < 25) {
      return {
        id: 'normal',
        name: 'Нормальная масса тела',
        color: '#4caf50',
        risk: 'Минимальный риск для здоровья'
      };
    } else if (bmi < 30) {
      return {
        id: 'overweight',
        name: 'Избыточная масса тела',
        color: '#ff9800',
        risk: 'Повышенный риск для здоровья'
      };
    } else if (bmi < 35) {
      return {
        id: 'obesity_1',
        name: 'Ожирение I степени',
        color: '#f44336',
        risk: 'Высокий риск для здоровья'
      };
    } else if (bmi < 40) {
      return {
        id: 'obesity_2',
        name: 'Ожирение II степени',
        color: '#d32f2f',
        risk: 'Очень высокий риск для здоровья'
      };
    } else {
      return {
        id: 'obesity_3',
        name: 'Ожирение III степени',
        color: '#b71c1c',
        risk: 'Экстремально высокий риск для здоровья'
      };
    }
  };

  // Расчет идеальных измерений
  const calculateIdealMeasurements = (height, gender, system) => {
    let idealWaist, idealHip;
    
    if (system === 'metric') {
      // Метрическая система (рост в см)
      if (gender === 'female') {
        idealWaist = height * 0.4;
        idealHip = height * 0.54;
      } else { // male
        idealWaist = height * 0.45;
        idealHip = height * 0.48;
      }
    } else {
      // Имперская система (рост в дюймах)
      if (gender === 'female') {
        idealWaist = height * 0.4;
        idealHip = height * 0.54;
      } else { // male
        idealWaist = height * 0.45;
        idealHip = height * 0.48;
      }
    }
    
    return {
      waist: idealWaist.toFixed(1),
      hip: idealHip.toFixed(1),
      whr: (idealWaist / idealHip).toFixed(2)
    };
  };

  // Генерация рекомендаций
  const generateRecommendations = (whr, bmi, gender, age, activityLevel) => {
    const recommendations = {
      nutrition: [],
      activity: [],
      lifestyle: []
    };
    
    // Определение категорий WHR и BMI для рекомендаций
    const isHighWHR = gender === 'female' ? whr > 0.85 : whr > 0.95;
    const isLowWHR = gender === 'female' ? whr < 0.75 : whr < 0.9;
    const isOverweight = bmi >= 25;
    const isUnderweight = bmi < 18.5;
    const isElderly = age > 60;
    
    // Рекомендации по питанию
    if (isHighWHR && isOverweight) {
      recommendations.nutrition.push(
        'Сократите потребление простых углеводов (сахар, белый хлеб, сладости)',
        'Увеличьте потребление белка (нежирное мясо, рыба, бобовые)',
        'Включите в рацион больше клетчатки (овощи, фрукты, цельнозерновые продукты)',
        'Ограничьте потребление насыщенных жиров (жирное мясо, сливочное масло)',
        'Добавьте полезные жиры (оливковое масло, авокадо, орехи)'
      );
    } else if (isLowWHR && isUnderweight) {
      recommendations.nutrition.push(
        'Увеличьте калорийность рациона за счет полезных продуктов',
        'Потребляйте больше белка для формирования мышечной массы',
        'Включите в рацион полезные жиры (орехи, авокадо, растительные масла)',
        'Рассмотрите добавление протеиновых коктейлей в рацион',
        'Питайтесь дробно, 5-6 раз в день'
      );
    } else {
      recommendations.nutrition.push(
        'Поддерживайте сбалансированное питание с достаточным количеством белков, жиров и углеводов',
        'Употребляйте не менее 400 г овощей и фруктов ежедневно',
        'Отдавайте предпочтение цельнозерновым продуктам вместо рафинированных',
        'Следите за размером порций, используйте принцип тарелки: 1/2 - овощи, 1/4 - белки, 1/4 - сложные углеводы'
      );
    }
    
    // Рекомендации по физической активности
    if (isHighWHR) {
      recommendations.activity.push(
        'Включите кардионагрузки 3-5 раз в неделю по 30-45 минут (ходьба, бег, плавание)',
        'Добавьте силовые тренировки 2-3 раза в неделю для укрепления мышц кора',
        'Уделяйте особое внимание упражнениям на мышцы пресса и поясницы',
        'Практикуйте интервальные тренировки для ускорения метаболизма'
      );
    } else if (isLowWHR) {
      recommendations.activity.push(
        'Сосредоточьтесь на силовых тренировках для верхней и нижней частей тела',
        'Уделяйте внимание упражнениям для мышц ягодиц и бедер',
        'Включите силовые тренировки 3-4 раза в неделю',
        'Ограничьте интенсивные кардионагрузки до 1-2 раз в неделю'
      );
    } else {
      recommendations.activity.push(
        'Поддерживайте регулярную физическую активность не менее 150 минут умеренной или 75 минут интенсивной нагрузки в неделю',
        'Сочетайте кардио и силовые тренировки для общего оздоровления',
        'Выбирайте активность, которая приносит удовольствие, для поддержания мотивации'
      );
    }
    
    // Рекомендации по образу жизни
    recommendations.lifestyle.push(
      'Поддерживайте водный баланс, употребляя не менее 1,5-2 литров воды в день',
      'Обеспечьте полноценный сон 7-8 часов для регуляции гормонов'
    );
    
    if (isHighWHR) {
      recommendations.lifestyle.push(
        'Практикуйте методы управления стрессом (медитация, дыхательные практики)',
        'Ограничьте потребление алкоголя, который способствует накоплению висцерального жира',
        'Следите за регулярностью приема пищи, избегайте переедания вечером'
      );
    }
    
    if (isElderly) {
      recommendations.lifestyle.push(
        'Регулярно проходите профилактические осмотры у врача',
        'Поддерживайте социальную активность для психологического благополучия',
        'Адаптируйте физическую нагрузку с учетом возрастных особенностей, отдавая предпочтение щадящим видам активности (плавание, йога, ходьба)'
      );
    }
    
    recommendations.note = 'Данные рекомендации носят общий характер. Перед внесением значительных изменений в питание и физическую активность проконсультируйтесь с врачом, особенно при наличии хронических заболеваний.';
    
    return recommendations;
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      gender: 'female',
      waist: '70',
      hip: '100',
      age: '30',
      height: '165',
      weight: '65',
      measurementSystem: 'metric',
      activityLevel: 'moderate'
    });
    setResults(null);
    setErrors({});
    setSaveResultName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const headers = [
      'Дата измерения', 
      'Пол', 
      'Возраст', 
      'Рост', 
      'Вес', 
      'Обхват талии', 
      'Обхват бедер', 
      'Индекс WHR', 
      'Категория WHR', 
      'ИМТ', 
      'Категория ИМТ'
    ];

    const unitLabel = formData.measurementSystem === 'metric' 
      ? { length: 'см', weight: 'кг' } 
      : { length: 'дюйм', weight: 'фунт' };

    const row = [
      new Date(results.date).toLocaleDateString('ru-RU'),
      results.personalData.gender === 'male' ? 'Мужской' : 'Женский',
      results.personalData.age,
      `${results.personalData.height} ${unitLabel.length}`,
      `${results.personalData.weight} ${unitLabel.weight}`,
      `${results.measurements.waist} ${unitLabel.length}`,
      `${results.measurements.hip} ${unitLabel.length}`,
      results.whr.toFixed(2),
      results.category.name,
      results.bmi.toFixed(2),
      results.bmiCategory.name
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), row.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'whr_результаты.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения результата
  const handleSaveResult = () => {
    if (showSaveDialog) {
      const savedRecord = saveResult(saveResultName || null);
      if (savedRecord) {
        alert(`Результат "${savedRecord.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveResultName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateResults();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Scale className="w-6 h-6 text-emerald-500" />
          Измерение WHR (соотношение талия-бедра)
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Раздел выбора пола и системы измерений */}
        <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Выбор пола */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Пол
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`flex items-center justify-center p-3 rounded-lg border transition-colors cursor-pointer
                  ${formData.gender === 'female' 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={formData.gender === 'female'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex flex-col items-center">
                    <User className="w-5 h-5 mb-1" />
                    <span>Женский</span>
                  </div>
                </label>
                
                <label className={`flex items-center justify-center p-3 rounded-lg border transition-colors cursor-pointer
                  ${formData.gender === 'male' 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={formData.gender === 'male'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex flex-col items-center">
                    <User className="w-5 h-5 mb-1" />
                    <span>Мужской</span>
                  </div>
                </label>
              </div>
              {errors.gender && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.gender}
                </p>
              )}
            </div>

            {/* Выбор системы измерения */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Система измерения
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`flex items-center justify-center p-3 rounded-lg border transition-colors cursor-pointer
                  ${formData.measurementSystem === 'metric' 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="measurementSystem"
                    value="metric"
                    checked={formData.measurementSystem === 'metric'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex flex-col items-center">
                    <Ruler className="w-5 h-5 mb-1" />
                    <span>Метрическая (см/кг)</span>
                  </div>
                </label>
                
                <label className={`flex items-center justify-center p-3 rounded-lg border transition-colors cursor-pointer
                  ${formData.measurementSystem === 'imperial' 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="measurementSystem"
                    value="imperial"
                    checked={formData.measurementSystem === 'imperial'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex flex-col items-center">
                    <Ruler className="w-5 h-5 mb-1" />
                    <span>Имперская (дюйм/фунт)</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Основные секции формы */}
        <MeasurementsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        <PersonalDataSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          activityLevels={activityLevels}
        />

        {/* Диалог сохранения результата */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveResultName}
                onChange={(e) => setSaveResultName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveResult}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<ActivitySquare className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать WHR'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveResult}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить результат
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;